import * as XLSX from 'xlsx';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BatchSiteRecord {
    rowIndex: number;
    salesforceId: string;
    siteId: number;
    revision: string;
    designNo: number;
    versionNo: number;
    productionEstimate: number;
    systemSize: number;
    multiplier: number;
    targetRoofCount: number;
    targetModuleCount: number;
}

export interface BatchSiteResult {
    rowIndex: number;
    salesforceId: string;
    siteId: number;
    status: 'pending' | 'processing' | 'completed' | 'error';
    error?: string;

    /**
     * Full Gemini module-fill input payload (prompt-context inputs + scenarios array).
     * Stored as JSON and exported into the batch results Excel.
     */
    geminiModuleFillingInputsJson: string;

    // Inputs captured from the AI process
    inputTotalRoofs: number;
    inputTotalModules: number;
    inputSystemMultiplier: number;
    inputActualWattage: number;
    inputRoofDetails: string;
    inputTreeCount: number;
    inputTreeLabels: string;
    inputShadeWeightsSummary: string;

    // AI outputs — selected combination
    aiSelectedCombination: string;
    aiSelectedRoofIds: string;
    aiRoofCount: number;
    aiModuleCount: number;
    aiProduction: number;
    matchQuality: string;

    // All combinations from AI
    allCombinations: string;

    // Timing
    processingTimeMs: number;
}

export interface BatchState {
    records: BatchSiteRecord[];
    results: BatchSiteResult[];
    currentIndex: number;
    isProcessing: boolean;
    startedAt: number | null;
    completedAt: number | null;
    /** Original Excel headers to preserve in output */
    originalHeaders: string[];
    /** Original raw row data (one array per row, matching originalHeaders) */
    originalRows: any[][];
}

// ─── Session storage keys ────────────────────────────────────────────────────

const BATCH_STATE_KEY = 'batchAIProcessingState';

// ─── Parse the Revision column ───────────────────────────────────────────────

export function parseRevision(rev: string): { designNo: number; versionNo: number } {
    const match = rev.match(/Design[:\s]*(\d+)\s*[-–]\s*Version[:\s]*(\d+)/i);
    if (!match) return { designNo: 1, versionNo: 1 };
    return { designNo: parseInt(match[1], 10), versionNo: parseInt(match[2], 10) };
}

// ─── Parse uploaded Excel ────────────────────────────────────────────────────

export interface ParsedExcel {
    records: BatchSiteRecord[];
    originalHeaders: string[];
    originalRows: any[][];
}

export function parseExcelFile(file: File): Promise<ParsedExcel> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                // Get headers from first row
                const rawAoA = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1 });
                const originalHeaders: string[] = (rawAoA[0] ?? []).map(String);
                const originalRows: any[][] = rawAoA.slice(1);

                const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet);

                const records: BatchSiteRecord[] = rows.map((row, idx) => {
                    const salesforceId = String(row['SalesforceId'] || row['salesforceId'] || row['SalesForceId'] || '').trim();
                    const siteId = Number(row['SiteId'] || row['siteId'] || row['Site_Id'] || 0);
                    const revision = String(row['Revision'] || row['revision'] || '');
                    const { designNo, versionNo } = parseRevision(revision);
                    const productionEstimate = Number(row['ProductionEstimate'] || row['Production'] || 0);
                    const systemSize = Number(row['SystemSize'] || row['System_Size'] || 0);
                    const multiplier = Number(row['Multiplier'] || row['multiplier'] || 0);
                    const targetRoofCount = Number(row['RoofCount'] || row['Roof_Count'] || 0);
                    const targetModuleCount = Number(row['ModuleCount'] || row['Module_Count'] || 0);

                    return {
                        rowIndex: idx + 1,
                        salesforceId,
                        siteId,
                        revision,
                        designNo,
                        versionNo,
                        productionEstimate,
                        systemSize,
                        multiplier,
                        targetRoofCount,
                        targetModuleCount,
                    };
                }).filter(r => r.salesforceId.length > 0);

                resolve({ records, originalHeaders, originalRows });
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
}

// ─── Batch state persistence (sessionStorage) ───────────────────────────────

export function saveBatchState(state: BatchState): void {
    try {
        sessionStorage.setItem(BATCH_STATE_KEY, JSON.stringify(state));
    } catch {
        // sessionStorage can overflow for large batches; silently continue
    }
}

export function loadBatchState(): BatchState | null {
    const raw = sessionStorage.getItem(BATCH_STATE_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as BatchState;
    } catch {
        return null;
    }
}

export function clearBatchState(): void {
    sessionStorage.removeItem(BATCH_STATE_KEY);
}

export function initBatchState(records: BatchSiteRecord[], originalHeaders: string[], originalRows: any[][]): BatchState {
    const results: BatchSiteResult[] = records.map(r => ({
        rowIndex: r.rowIndex,
        salesforceId: r.salesforceId,
        siteId: r.siteId,
        status: 'pending',
        geminiModuleFillingInputsJson: '',
        inputTotalRoofs: 0,
        inputTotalModules: 0,
        inputSystemMultiplier: 0,
        inputActualWattage: 0,
        inputRoofDetails: '',
        inputTreeCount: 0,
        inputTreeLabels: '',
        inputShadeWeightsSummary: '',
        aiSelectedCombination: '',
        aiSelectedRoofIds: '',
        aiRoofCount: 0,
        aiModuleCount: 0,
        aiProduction: 0,
        matchQuality: '',
        allCombinations: '',
        processingTimeMs: 0,
    }));

    return {
        records,
        results,
        currentIndex: 0,
        isProcessing: true,
        startedAt: Date.now(),
        completedAt: null,
        originalHeaders,
        originalRows,
    };
}

// ─── Parse RECOMMENDED_ROOF_COMBINATIONS from AI result ──────────────────────

export interface RoofCombination {
    name: string;
    roofFaceIds: string[];
}

export function parseRoofCombinations(aiResult: string): RoofCombination[] {
    const combos: RoofCombination[] = [];
    const match = aiResult.match(/RECOMMENDED_ROOF_COMBINATIONS:\s*(.+)/);
    if (!match) return combos;

    const parts = match[1].split(';').map(s => s.trim()).filter(Boolean);
    for (const part of parts) {
        const pipeIdx = part.indexOf('|');
        if (pipeIdx < 0) continue;
        const name = part.substring(0, pipeIdx).trim();
        const ids = part.substring(pipeIdx + 1).split(',').map(s => s.trim()).filter(Boolean);
        combos.push({ name, roofFaceIds: ids });
    }
    return combos;
}

/**
 * Given the AI roof combinations and the roofInfoArr from the store,
 * find the combination closest to the target RoofCount and ModuleCount.
 */
export function findBestMatchingCombination(
    combos: RoofCombination[],
    roofInfoArr: Array<{ roofFaceId: string; modules?: string | number; [k: string]: any }>,
    targetRoofCount: number,
    targetModuleCount: number
): { combo: RoofCombination | null; moduleCount: number; roofCount: number; matchQuality: string } {
    if (!combos.length) return { combo: null, moduleCount: 0, roofCount: 0, matchQuality: 'No combinations found' };

    let bestCombo: RoofCombination | null = null;
    let bestModuleCount = 0;
    let bestRoofCount = 0;
    let bestScore = Infinity;

    for (const combo of combos) {
        const roofCount = combo.roofFaceIds.length;
        let moduleCount = 0;
        for (const id of combo.roofFaceIds) {
            const roof = roofInfoArr.find(r => r.roofFaceId === id);
            if (roof) {
                moduleCount += typeof roof.modules === 'number' ? roof.modules : Number(roof.modules || 0);
            }
        }

        const roofDiff = Math.abs(roofCount - targetRoofCount);
        const moduleDiff = Math.abs(moduleCount - targetModuleCount);
        const score = moduleDiff * 2 + roofDiff * 5;

        if (score < bestScore) {
            bestScore = score;
            bestCombo = combo;
            bestModuleCount = moduleCount;
            bestRoofCount = roofCount;
        }
    }

    let matchQuality = 'Exact';
    if (bestRoofCount !== targetRoofCount || bestModuleCount !== targetModuleCount) {
        const roofDiff = Math.abs(bestRoofCount - targetRoofCount);
        const moduleDiff = Math.abs(bestModuleCount - targetModuleCount);
        if (roofDiff <= 1 && moduleDiff <= 3) matchQuality = 'Close';
        else matchQuality = `Off (Roof: ${bestRoofCount > targetRoofCount ? '+' : ''}${bestRoofCount - targetRoofCount}, Modules: ${bestModuleCount > targetModuleCount ? '+' : ''}${bestModuleCount - targetModuleCount})`;
    }

    return { combo: bestCombo, moduleCount: bestModuleCount, roofCount: bestRoofCount, matchQuality };
}

// ─── Capture AI results from the current viewer state ────────────────────────

export function captureAIResults(
    record: BatchSiteRecord,
    aiResult: string,
    storeState: any,
    startTime: number
): BatchSiteResult {
    const mapCoordinates = storeState?.mapCoordinates;
    const rawLat = typeof mapCoordinates?.latitude === 'number' ? mapCoordinates.latitude : null;
    const rawLon = typeof mapCoordinates?.longitude === 'number' ? mapCoordinates.longitude : null;
    // Store initializes mapCoordinates to 0/0; treat that as "unset".
    const latitude = rawLat != null && Number.isFinite(rawLat) && rawLat !== 0 ? rawLat : null;
    const longitude = rawLon != null && Number.isFinite(rawLon) && rawLon !== 0 ? rawLon : null;
    const siteAddress = storeState?.siteAddress ?? null;
    const layoutVariantsSummary = storeState?.geminiModuleFillingLayoutVariantsSummary ?? '';
    const scenariosForGemini = storeState?.aiModuleFillingScenarios ?? [];

    let geminiModuleFillingInputsJson = '';
    try {
        geminiModuleFillingInputsJson = JSON.stringify(
            {
                latitude,
                longitude,
                siteAddress,
                layoutVariantsSummary,
                scenarios: scenariosForGemini,
            },
            null,
            2,
        );
    } catch {
        // If serialization fails (should be rare), leave the JSON empty.
        geminiModuleFillingInputsJson = '';
    }

    const roofInfoArr = storeState.roofInfoArr ?? [];
    const scenarios = storeState.aiModuleFillingScenarios ?? [];
    const baseScenario = scenarios[0];
    const actualWattage = storeState.actualWattage ?? 0;
    const multiplier = storeState.multiplier ?? 0;
    const modeCount = storeState.modeCount ?? 0;

    const roofCount = roofInfoArr.filter((r: any) => Number(r.modules || 0) > 0).length;
    const roofDetails = roofInfoArr
        .filter((r: any) => Number(r.modules || 0) > 0)
        .map((r: any) => `Roof ${r.roofNum}: ${r.modules} modules, SA=${r.solarAccess}, Mult=${r.roofMultiplier}`)
        .join(' | ');

    const treeMultipliers = baseScenario?.treeMultipliers ?? [];
    const treeCount = treeMultipliers.length;
    const treeLabels = treeMultipliers.map((t: any) => t.treeLabel).join(', ');

    const shadeWeights = baseScenario?.shadeWeights ?? [];
    const shadeWeightsSummary = shadeWeights
        .map((sw: any) => `Roof ${sw.roofNum}: SA=${sw.solarAccess}, Mult=${sw.roofMultiplier}, Modules=${sw.moduleCount ?? 0}`)
        .join(' | ');

    const combos = parseRoofCombinations(aiResult);
    const allCombinationsStr = combos.map(c => `${c.name}: [${c.roofFaceIds.join(',')}]`).join(' ; ');

    const { combo, moduleCount: aiModuleCount, roofCount: aiRoofCount } =
        findBestMatchingCombination(combos, roofInfoArr, record.targetRoofCount, record.targetModuleCount);

    let aiProduction = 0;
    if (combo && baseScenario) {
        const totalModMult = shadeWeights.reduce(
            (s: number, r: any) => s + (r.moduleCount ?? 0) * (r.roofMultiplier ?? 0), 0
        );
        for (const id of combo.roofFaceIds) {
            const sw = shadeWeights.find((r: any) => r.roofFaceId === id);
            if (sw && totalModMult > 0) {
                aiProduction += ((sw.moduleCount ?? 0) * (sw.roofMultiplier ?? 0) / totalModMult) * (baseScenario.actualWattage ?? 0);
            }
        }
    }

    // Close only if BOTH:
    // 1) AI roofs/modules are close to targets
    // 2) AI production is close to target production
    let matchQuality = 'Off';
    if (!combo) {
        matchQuality = 'No combinations found';
    } else {
        const roofDiff = Math.abs(aiRoofCount - record.targetRoofCount);
        const moduleDiff = Math.abs(aiModuleCount - record.targetModuleCount);
        const roofModuleExact = roofDiff === 0 && moduleDiff === 0;
        const roofModuleClose = roofDiff <= 1 && moduleDiff <= 3;

        const targetProduction = record.productionEstimate ?? 0;
        const productionDiff = Math.abs(aiProduction - targetProduction);
        const productionTolerance = Math.max(1, Math.abs(targetProduction) * 0.05); // 5% or 1 (absolute)
        const productionClose = productionDiff <= productionTolerance;

        if (roofModuleExact && productionClose) {
            matchQuality = 'Exact';
        } else if (roofModuleClose && productionClose) {
            matchQuality = 'Close';
        } else {
            const roofDelta = aiRoofCount - record.targetRoofCount;
            const moduleDelta = aiModuleCount - record.targetModuleCount;
            const productionDelta = Math.round(aiProduction) - Math.round(targetProduction);
            matchQuality = `Off (Roof: ${roofDelta >= 0 ? '+' : ''}${roofDelta}, Modules: ${moduleDelta >= 0 ? '+' : ''}${moduleDelta}, Production: ${productionDelta >= 0 ? '+' : ''}${productionDelta})`;
        }
    }

    return {
        rowIndex: record.rowIndex,
        salesforceId: record.salesforceId,
        siteId: record.siteId,
        status: 'completed',
        geminiModuleFillingInputsJson,
        inputTotalRoofs: roofCount,
        inputTotalModules: modeCount,
        inputSystemMultiplier: multiplier,
        inputActualWattage: actualWattage,
        inputRoofDetails: roofDetails,
        inputTreeCount: treeCount,
        inputTreeLabels: treeLabels,
        inputShadeWeightsSummary: shadeWeightsSummary,
        aiSelectedCombination: combo?.name ?? 'N/A',
        aiSelectedRoofIds: combo?.roofFaceIds.join(', ') ?? '',
        aiRoofCount,
        aiModuleCount,
        aiProduction: Math.round(aiProduction),
        matchQuality,
        allCombinations: allCombinationsStr,
        processingTimeMs: Date.now() - startTime,
    };
}

// ─── Generate results Excel (original columns + AI result columns) ───────────

const AI_RESULT_HEADERS_BASE = [
    'Status',
    'AI: Selected Combination',
    'AI: Roof Count',
    'AI: Module Count',
    'AI: Est. Production',
    'Match Quality',
    'Input: Total Roofs (MaxFill)',
    'Input: Total Modules (MaxFill)',
    'Input: System Multiplier',
    'Input: Actual Wattage (MaxFill)',
    'Input: Tree Count',
    'Input: Tree Labels',
    'Input: Shade Weights Summary',
    'Input: Roof Details',
    'AI: All Combinations',
    'Error',
    'Processing Time (s)',
];

export function generateResultsExcel(batchState: BatchState): void {
    const { results, records } = batchState;
    const origHeaders = Array.isArray(batchState.originalHeaders) && batchState.originalHeaders.length > 0
        ? batchState.originalHeaders
        : ['SalesforceId', 'SiteId', 'Revision', 'ProductionEstimate', 'SystemSize', 'Multiplier', 'RoofCount', 'ModuleCount'];
    const origRows = Array.isArray(batchState.originalRows) && batchState.originalRows.length > 0
        ? batchState.originalRows
        : null;

    const combinedHeaders = [...origHeaders, ...AI_RESULT_HEADERS_BASE];

    const dataRows = results.map((result, idx) => {
        const record = records[idx];

        // Use original row data if available, otherwise reconstruct from parsed record
        let origRow: any[];
        if (origRows && origRows[idx]) {
            origRow = [...origRows[idx]];
        } else if (record) {
            origRow = [
                record.salesforceId, record.siteId, record.revision,
                record.productionEstimate, record.systemSize, record.multiplier,
                record.targetRoofCount, record.targetModuleCount,
            ];
        } else {
            origRow = new Array(origHeaders.length).fill('');
        }

        while (origRow.length < origHeaders.length) origRow.push('');

        const aiCols = result ? [
            result.status,
            result.aiSelectedCombination || '',
            result.status === 'completed' ? result.aiRoofCount : '',
            result.status === 'completed' ? result.aiModuleCount : '',
            result.status === 'completed' ? result.aiProduction : '',
            result.matchQuality || '',
            result.status === 'completed' ? result.inputTotalRoofs : '',
            result.status === 'completed' ? result.inputTotalModules : '',
            result.status === 'completed' ? result.inputSystemMultiplier : '',
            result.status === 'completed' ? result.inputActualWattage : '',
            result.status === 'completed' ? result.inputTreeCount : '',
            result.inputTreeLabels || '',
            result.inputShadeWeightsSummary || '',
            result.inputRoofDetails || '',
            result.allCombinations || '',
            result.error ?? '',
            result.processingTimeMs ? Math.round(result.processingTimeMs / 1000) : '',
        ] : new Array(AI_RESULT_HEADERS_BASE.length).fill('');

        return [...origRow, ...aiCols];
    });

    const wsData = [combinedHeaders, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-size columns
    const colWidths = combinedHeaders.map((h, i) => {
        let maxLen = String(h).length;
        for (const row of dataRows) {
            const cellLen = String(row[i] ?? '').length;
            if (cellLen > maxLen) maxLen = cellLen;
        }
        return { wch: Math.min(maxLen + 2, 60) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'AI Design Results');

    // ─── Gemini Inputs sheet (logical sections, keyed by SalesforceId) ─────────
    type GeminiInputs = {
        latitude: number | null;
        longitude: number | null;
        siteAddress: string | null;
        layoutVariantsSummary?: string;
        scenarios?: any[];
    };

    const geminiSheetRows: any[][] = [
        ['SalesforceId', 'SiteId', 'Section', 'Key', 'Value'],
    ];

    const pushRow = (sfid: string, siteId: number | string, section: string, key: string, value: any) => {
        const v =
            value == null ? '' :
            typeof value === 'string' ? value :
            typeof value === 'number' || typeof value === 'boolean' ? String(value) :
            (() => {
                try { return JSON.stringify(value); } catch { return String(value); }
            })();
        geminiSheetRows.push([sfid, siteId, section, key, v]);
    };

    results.forEach((r, idx) => {
        const rec = records[idx];
        const sfid = r?.salesforceId ?? rec?.salesforceId ?? '';
        const siteId = r?.siteId ?? rec?.siteId ?? '';
        if (!sfid) return;

        let parsed: GeminiInputs | null = null;
        try {
            parsed = r?.geminiModuleFillingInputsJson ? (JSON.parse(r.geminiModuleFillingInputsJson) as GeminiInputs) : null;
        } catch {
            parsed = null;
        }

        // Always include a marker row so it's obvious when inputs are missing.
        if (!parsed) {
            pushRow(sfid, siteId, 'Gemini', 'inputs', '(missing / not captured)');
            return;
        }

        // Site / location
        pushRow(sfid, siteId, 'Site', 'siteAddress', parsed.siteAddress ?? '');
        pushRow(sfid, siteId, 'Site', 'latitude', parsed.latitude ?? '');
        pushRow(sfid, siteId, 'Site', 'longitude', parsed.longitude ?? '');
        pushRow(sfid, siteId, 'Site', 'layoutVariantsSummary', parsed.layoutVariantsSummary ?? '');

        const scenarios = Array.isArray(parsed.scenarios) ? parsed.scenarios : [];
        const base = scenarios[0] ?? null;

        // AHJ / constraints (from base scenario, if present)
        if (base) {
            pushRow(sfid, siteId, 'Constraints', 'ahjRulesSummary', base.ahjRulesSummary ?? '');
            pushRow(sfid, siteId, 'Constraints', 'setbackSummary', base.setbackSummary ?? '');
            pushRow(sfid, siteId, 'Constraints', 'pathwaySummary', base.pathwaySummary ?? '');
            pushRow(sfid, siteId, 'Constraints', 'drawingSummary', base.drawingSummary ?? '');
            pushRow(sfid, siteId, 'Constraints', 'moduleTypeSummary', base.moduleTypeSummary ?? '');
            pushRow(sfid, siteId, 'Constraints', 'treeMetaSummary', base.treeMetaSummary ?? '');
        }

        // Tree multipliers (prefer base scenario)
        const treeMultipliers = Array.isArray(base?.treeMultipliers) ? base.treeMultipliers : [];
        treeMultipliers.forEach((t: any, tIdx: number) => {
            const id = t?.treeId ?? '';
            const label = t?.treeLabel ?? '';
            const impact = t?.treeImpactMulti ?? '';
            pushRow(sfid, siteId, 'TreeMultipliers', `tree[${tIdx}]`, { treeId: id, treeLabel: label, treeImpactMulti: impact });
        });

        // Scenario summary rows (one row per scenario; concise + readable)
        scenarios.forEach((s: any, sIdx: number) => {
            const shadeWeights = Array.isArray(s?.shadeWeights) ? s.shadeWeights : [];
            const roofs = shadeWeights.length;
            const points = shadeWeights.reduce((sum: number, sw: any) => sum + (Array.isArray(sw?.shadePoints) ? sw.shadePoints.length : 0), 0);
            pushRow(sfid, siteId, 'Scenarios', `scenario[${sIdx}]`, {
                treeLabel: s?.treeLabel ?? null,
                treeId: s?.treeId ?? null,
                treeIds: s?.treeIds ?? null,
                cutCost: s?.cutCost ?? null,
                systemMultiplier: s?.systemMultiplier ?? null,
                actualWattage: s?.actualWattage ?? null,
                roofs,
                shadePoints: points,
            });
        });

        // Shade weights (per scenario × roof) — readable summary, avoids huge raw shadePoints arrays.
        scenarios.forEach((s: any, sIdx: number) => {
            const shadeWeights = Array.isArray(s?.shadeWeights) ? s.shadeWeights : [];
            shadeWeights.forEach((sw: any, rIdx: number) => {
                const pts = Array.isArray(sw?.shadePoints) ? sw.shadePoints : [];
                const ptHashVals = pts
                    .map((p: any) => (typeof p?.ptHashVal === 'number' ? p.ptHashVal : null))
                    .filter((v: any) => typeof v === 'number' && Number.isFinite(v)) as number[];

                let minPt: number | null = null;
                let maxPt: number | null = null;
                let avgPt: number | null = null;
                if (ptHashVals.length) {
                    minPt = Math.min(...ptHashVals);
                    maxPt = Math.max(...ptHashVals);
                    const sum = ptHashVals.reduce((a, b) => a + b, 0);
                    avgPt = sum / ptHashVals.length;
                }

                const roofFaceId = sw?.roofFaceId ?? '';
                const roofNum = sw?.roofNum ?? null;
                const key = `scenario[${sIdx}].roof[${roofNum != null ? roofNum : rIdx}]`;
                pushRow(sfid, siteId, 'ShadeWeights', key, {
                    roofFaceId,
                    roofNum,
                    moduleCount: sw?.moduleCount ?? null,
                    solarAccess: sw?.solarAccess ?? null,
                    roofMultiplier: sw?.roofMultiplier ?? null,
                    actualProduction: sw?.actualProduction ?? null,
                    shadePointsCount: pts.length,
                    ptHashValMin: minPt,
                    ptHashValAvg: avgPt != null ? Number(avgPt.toFixed(2)) : null,
                    ptHashValMax: maxPt,
                });
            });
        });
    });

    const wsGemini = XLSX.utils.aoa_to_sheet(geminiSheetRows);
    // Make the text columns wider for readability.
    wsGemini['!cols'] = [
        { wch: 22 }, // SalesforceId
        { wch: 10 }, // SiteId
        { wch: 16 }, // Section
        { wch: 22 }, // Key
        { wch: 90 }, // Value
    ];
    XLSX.utils.book_append_sheet(wb, wsGemini, 'Gemini Inputs');

    XLSX.writeFile(wb, `BatchAI_Results_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
